module Client.Page.User.MySensorsData

open Client.Components
open Client.Components.Select
open Client.Infrastructure.Api
open Client.Domain
open Client.Infrastructure
open Client.Domain.MeasuredValueString
open Client.DomainTypes
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Client.Map.SensorPopup
open Elmish
open Fable.FontAwesome
open Fable.React
open Fable.React.Props
open Fulma
open Microsoft.FSharp.Core
open Shared
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.MapSensorData
open Shared.DtoTypes.MapSensorData
open Shared.Infrastructure

let private errorConfirmationConfig: ErrorConfirmation.Config<Msg> = {
    Handler = ErrorConfirmation.createHandler (MySensorDataMsg.Reload >> MySensorsData)
    TimeoutMilliseconds = 5000
}

type SensorTypeFilter = {
    AvailableTypes: MapSensorType list
    SelectedType: MapSensorType option
}

type SensorTypeFilterModel =
    | NoSensors
    | Types of SensorTypeFilter

type DataModel = {
    Sensors: MapSensorDataResponse
    FilteredSensors: MapSensorDataResponse
    Filter: SensorTypeFilterModel
    Session: UserSession
    IsRefreshRunning: bool
}

type Model = Loadable<DataModel, UserSession>

let private refreshCmd sessionKey =
    let request = {
        SessionKey = sessionKey
        Data = ()
    }

    Cmd.OfAsync.either
        api.getMyMapSensorsData
        request
        (MySensorDataMsg.DataReceived >> MySensorsData)
        (MySensorDataMsg.LoadingFailed >> MySensorsData)

let init (session: UserSession) =
    Loadable.Loading session, refreshCmd session.SessionKey

let private getSession (model: Model) =
    match model with
    | Loadable.Loading session -> session
    | Loadable.Data dataModel -> dataModel.Session
    | _ -> failwith "That should never happend"

let private getSessionKey (model: Model) =
    getSession model |> fun session -> session.SessionKey

let private getSensorTypeFilterModel (previousSelectedType: MapSensorType option) (data: MapSensorDataResponse) =
    let availableGroupTypes =
        data.Groups
        |> List.collect (fun x -> x.Senors)
        |> List.map (fun x -> x.SensorType)

    let mySensorTypes = data.MySensors |> List.map (fun x -> x.SensorType)

    let availableSensorTypes =
        List.append availableGroupTypes mySensorTypes
        |> List.distinct
        |> List.sortBy MapSensorType.toString

    if List.isEmpty availableSensorTypes then
        NoSensors
    else
        let newSelectedType =
            previousSelectedType
            |> Option.bind (fun mapSensorType ->
                if List.contains mapSensorType availableSensorTypes then
                    Some mapSensorType
                else
                    None
            )

        Types {
            AvailableTypes = availableSensorTypes
            SelectedType = newSelectedType
        }

let private refreshSensorTypeFilterModel (model: DataModel) =
    let selectedType =
        match model.Filter with
        | Types filter -> filter.SelectedType
        | NoSensors -> None

    { model with Filter = getSensorTypeFilterModel selectedType model.Sensors }

let private filterSensors (model: DataModel) =
    match model.Filter with
    | Types filter ->
        match filter.SelectedType with
        | Some sensorType -> { model with FilteredSensors = filterMapSensorDataResponseByType sensorType model.Sensors }
        | None -> { model with FilteredSensors = model.Sensors }
    | NoSensors -> model

let update (msg: MySensorDataMsg) (model: Model) =
    match msg, model with
    | MySensorDataMsg.DataReceived result, Loadable.Loading session ->
        match result with
        | Ok data ->
            Loadable.Data {
                Sensors = data
                FilteredSensors = data
                Filter = getSensorTypeFilterModel None data
                Session = session
                IsRefreshRunning = false
            },
            Cmd.none
        | Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MySensorDataMsg.DataReceived result, Loadable.Data dataModel ->
        match result with
        | Ok data ->
            let newDataModel =
                {
                    dataModel with
                        IsRefreshRunning = false
                        Sensors = data
                }
                |> refreshSensorTypeFilterModel
                |> filterSensors

            Loadable.Data newDataModel, Cmd.none
        | Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | RefreshData, Loadable.Data dataModel ->
        Loadable.Data({ dataModel with IsRefreshRunning = true }), refreshCmd (getSessionKey model)
    | MySensorDataMsg.LoadingFailed _, _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | MySensorDataMsg.FilterChanged filter, Loadable.Data dataModel ->
        let filter =
            match dataModel.Filter with
            | Types currentFilter -> Types { currentFilter with SelectedType = filter }
            | NoSensors -> NoSensors

        let newDataModel = { dataModel with Filter = filter } |> filterSensors

        Loadable.Data newDataModel, Cmd.none
    | _ -> model, Cmd.none

let currentDataLevelItem label value =
    Level.item [ Level.Item.HasTextCentered ] [
        div [] [
            Level.heading [] [ str label ]
            Level.title [] [ str value ]
        ]
    ]

let currentAirDataBox (data: AirSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Aktuelle Temperatur" (temperatureToString data.AirTemperature)
        currentDataLevelItem "Aktuelle Luftfeuchtigkeit" (percentageToString data.AirHumidity)
        currentDataLevelItem "Feuchttemperatur" (temperatureToString data.HumidTemperature)
    ]
]

let currentGroundDataBox (data: SoilSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Aktuelle Bodenfeuchtigkeit" (percentageToString data.SoilMoisture)
        currentDataLevelItem "Aktuelle Temperatur" (temperatureToString data.SoilTemperature)
    ]
]

let currentRainfallDataBox (data: RainFallSensorData) =
    let maybeRainFallToString = Option.map rainFallToString >> Option.defaultValue "-"

    [
        Level.level [] [
            currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
            currentDataLevelItem "Regen aktuell" (rainFallToString data.CurrentRainFall)
            currentDataLevelItem "Regen letzte Stunde" (maybeRainFallToString data.RainFallLastHour)
            currentDataLevelItem "Regen letzte 24 Stunden" (maybeRainFallToString data.RainFallLast24h)
        ]
    ]

let currentSoilPhDataBox (data: SoilPhSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "PH-Wert" (sprintf "%.2f" data.SoilPh)
        currentDataLevelItem "Bodentemperatur" (temperatureToString data.SoilTemperature)
    ]
]

let currentAverageWindSpeedDataBox (data: AverageWindSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Ø Windgeschwindigkeit" (windSpeedToString data.AverageWindSpeed)
    ]
]

let currentLeafletMoistureDataBox (data: LeafletMoistureSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Blattnässe" (percentageToString data.LeafletMoisture)
        currentDataLevelItem "Blatttemperatur" (temperatureToString data.LeafletTemperature)
    ]
]

let currentWeatherStationDataBox (data: WeatherStationSensorData) = [
    let (windSpeed, windDirection) =
        match data.CurrentWind with
        | Wind wind -> (windSpeedToString wind.WindSpeed, windDirectionToString wind.WindDirection)
        | NoWind -> ("-", "-")


    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toShortString data.Date.LocalDateTime)
        currentDataLevelItem "Aktuelle Temperatur" (temperatureToString data.AirTemperature)
        currentDataLevelItem "Aktuelle Luftfeuchtigkeit" (percentageToString data.AirHumidity)
        currentDataLevelItem "Aktuelle Windgeschwindigkeit" windSpeed
        currentDataLevelItem "Aktuelle Windrichtung" windDirection
    ]
]

let currentLiquidLevelDataBox (data: LiquidLevelData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Füllstand" (liquidLevelToString data.LiquidLevel)
    ]
]

let currentFourTimesSoilDataBox (data: FourTimesSoilSensorData) =
    let mapOptionalFloat = fun f x -> x |> Option.map f |> Option.defaultValue "-"

    [
        Level.level [] [
            currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        ]

        Level.level [] [
            currentDataLevelItem "Aktuelle Bodenfeuchtigkeit #1" (percentageToString data.SoilMoisture1)
            currentDataLevelItem "Aktuelle Temperatur #1" (temperatureToString data.SoilTemperature1)
            currentDataLevelItem "Aktuelle Leitfähigkeit #1" (data.SoilConductivity1 |> conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #2"
                (data.SoilMoisture2 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #2"
                (data.SoilTemperature2 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #2"
                (data.SoilConductivity2 |> mapOptionalFloat conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #3"
                (data.SoilMoisture3 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #3"
                (data.SoilTemperature3 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #3"
                (data.SoilConductivity3 |> mapOptionalFloat conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #4"
                (data.SoilMoisture4 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #4"
                (data.SoilTemperature4 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #4"
                (data.SoilConductivity4 |> mapOptionalFloat conductivityToString)
        ]
    ]

let currentPressureDataBox (data: PressureData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Druck" (pressureToString data.Pressure)
    ]
]

let currentWaterQualityDataBox (data: WaterQualityData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Leitfähigkeit" (data.EC |> Option.map conductivityToString |> Option.defaultValue "-")
        currentDataLevelItem "PH-Wert" (data.PH |> Option.map phToString |> Option.defaultValue "-")
    ]
]

let private createDetailsSensorNameLink props (id: int) (name: string) dispatch =
    let customProps = [
        id |> Route.MySensSensor |> Clickable.onClickGoToRoute dispatch |> OnClick :> IHTMLProp
        Href(id |> Route.MySensSensor |> Routing.routeToUrl)
    ]

    a (List.append customProps props) [ str name ]

let private createActiveBoxTitleLink dispatch (sensor: MyMapSensorDataDto) : ReactElement =
    let sensorLinkStyle =
        Style [
            Color(getSensorNameLinkColor sensor.Data)
            TextDecoration "underline"
        ]
        :> IHTMLProp

    createDetailsSensorNameLink [ sensorLinkStyle ] sensor.Id sensor.Name dispatch

let private createBoxSubTitle (maybeSecondName: string option) =
    match maybeSecondName with
    | Some secondName -> Some(Heading.h5 [ Heading.IsSubtitle ] [ str secondName ])
    | None -> None

let private wrapBoxTitles (title: ReactElement) (maybeSubtitle: ReactElement option) =
    div [ Class "mb-5" ] [
        Heading.h3 [] [ title ]
        match maybeSubtitle with
        | Some subtitle -> subtitle
        | None -> ()
    ]

let private wrapBoxHeading (headingContent: ReactElement list) : ReactElement =
    div
        [
            Style [
                TextAlign TextAlignOptions.Center
                Display DisplayOptions.Flex
                AlignItems AlignItemsOptions.Center
                JustifyContent "center"
            ]
        ]
        headingContent

let private createMapLink dispatch (sensor: MyMapSensorDataDto) : ReactElement =
    a [
        sensor.Location
        |> Some
        |> Route.SensorMap
        |> Clickable.onClickGoToRoute dispatch
        |> OnClick
    ] [
        Icon.icon [
            Icon.CustomClass "mb-5"
            Icon.CustomClass "ml-2"
        ] [
            Fa.i [ Fa.Size Fa.FaLarge; Fa.Solid.MapMarkedAlt ] []
        ]
    ]

let private dataTextContent (text: string) (icon: Fa.IconOption) = [
    div [
        Style [
            Display DisplayOptions.Flex
            FlexDirection FlexDirection.Column
            JustifyContent "center"
            AlignItems AlignItemsOptions.Center
        ]
    ] [
        Fa.span [ icon ] []
        p [ Class "ml-2" ] [ str text ]
    ]
]

let private activeSensorDataContent (sensor: MyMapSensorDataDto) : ReactElement list =
    match sensor.Data with
    | AirData air -> currentAirDataBox air
    | SoilData ground -> currentGroundDataBox ground
    | RainFallData rainFall -> currentRainfallDataBox rainFall
    | SoilPhData data -> currentSoilPhDataBox data
    | AverageWindSpeedData data -> currentAverageWindSpeedDataBox data
    | LeafletMoistureData data -> currentLeafletMoistureDataBox data
    | WeatherStationData data -> currentWeatherStationDataBox data
    | LiquidLevelData data -> currentLiquidLevelDataBox data
    | FourTimesSoilData data -> currentFourTimesSoilDataBox data
    | PressureData data -> currentPressureDataBox data
    | WaterQualityData data -> currentWaterQualityDataBox data
    | NoDataAvailable -> dataTextContent "Keine aktuellen Daten gefunden" Fa.Solid.Hourglass
    | _ -> []

let private sensorBoxContent dispatch (sensor: MyMapSensorDataDto) : ReactElement list =
    let title = createActiveBoxTitleLink dispatch sensor
    let subtitle = createBoxSubTitle sensor.SecondName
    let titles = wrapBoxTitles title subtitle

    let boxHeading = wrapBoxHeading [ titles ]

    let mapLink = createMapLink dispatch sensor

    let boxHeading = wrapBoxHeading [ boxHeading; mapLink ]

    let boxData = activeSensorDataContent sensor

    boxHeading :: boxData

let private surroundingBox (content: ReactElement list) =
    Box.box' [ GenericOption.CustomClass "grey_box" ] [
        Columns.columns [] [
            Column.column [ Column.Width(Screen.All, Column.IsFull) ] content
        ]
    ]

let private surroundedSensorBox dispatch (sensor: MyMapSensorDataDto) =
    sensorBoxContent dispatch sensor |> surroundingBox

let private groupView dispatch (group: UserGroupMapSensorsDto) =
    let sensorBoxes =
        group.Senors
        |> List.sortBy (fun sensor -> sensor.Name)
        |> List.map (surroundedSensorBox dispatch)

    let heading = sprintf "Gruppe: %s" group.Name

    [
        PageSkeleton.centeredHeading heading
        yield! sensorBoxes
    ]

let private filterView dispatch (model: SensorTypeFilter) =
    Field.div [] [
        Label.label [] [ str "Nach Sensor-Typ filtern" ]
        Control.div [ Control.HasIconLeft ] [
            Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

            MapSensorTypeFilter.view
                model.AvailableTypes
                model.SelectedType
                (MySensorDataMsg.FilterChanged >> MySensorsData >> dispatch)
        ]
    ]

let dataView dispatch (model: DataModel) =
    let mySensorBoxes =
        model.FilteredSensors.MySensors
        |> List.sortBy (fun x -> x.Name)
        |> List.map (sensorBoxContent dispatch >> surroundingBox)

    let groupSensorBoxes =
        model.FilteredSensors.Groups
        |> List.sortBy (fun group -> group.Name)
        |> List.collect (groupView dispatch)

    let button =
        Button.button [
            Button.Color Color.IsLink
            Button.IsLoading model.IsRefreshRunning
            Button.OnClick(fun _ -> dispatch (MySensorsData MySensorDataMsg.RefreshData))
        ] [
            Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.Sync ] [] ]
            span [] [ str "Aktualisieren" ]
        ]

    let buttonRow =
        Level.level [
            Level.Level.Modifiers [
                Modifier.Spacing(Spacing.MarginBottom, Spacing.Is3)
            ]
        ] [
            Level.left [] [
                Level.item [] [
                    match model.Filter with
                    | NoSensors -> ()
                    | Types filter -> filterView dispatch filter
                ]
            ]
            Level.right [] [ Level.item [] [ button ] ]
        ]

    let content = [
        buttonRow
        yield! mySensorBoxes
        yield! groupSensorBoxes
    ]

    Container.container [ Container.isFullWidth ] content

let view (model: Model) dispatch = Loadable.view (dataView dispatch) model